import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Card,
  CardBody,
  Heading,
  HStack,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchOrganizationSurveysPayload,
  normalizeOrganizationSurveys,
} from "../organization/organizationSurveysApi";
import { getNetworkOrganizations } from "./networkApi";
import { formatOrganizationSurveyTitle } from "../../utils/organizationSurveyHelper";

const getSurveyId = (survey) => survey?.organization_survey_id ?? survey?.id ?? null;

const NetworkResultsPage = () => {
  const { networkId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const mutedColor = useColorModeValue("gray.600", "gray.400");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [entries, setEntries] = useState([]);

  const loadResults = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const organizations = await getNetworkOrganizations(networkId);
      const surveyPayloads = await Promise.all(
        organizations.map(async (organization) => {
          try {
            const payload = await fetchOrganizationSurveysPayload({ organizationId: organization.id });
            return normalizeOrganizationSurveys(payload).map((survey) => ({
              ...survey,
              organization,
              organization_survey_id: getSurveyId(survey),
            }));
          } catch (err) {
            return [];
          }
        })
      );
      setEntries(surveyPayloads.flat().filter((survey) => survey.organization_survey_id));
    } catch (err) {
      console.error("Error loading network results:", err);
      setError("Could not load network results.");
      toast({ title: "Could not load network results", status: "error", duration: 4000, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [networkId, toast]);

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  const sortedEntries = useMemo(
    () => [...entries].sort((a, b) =>
      `${a.organization?.name || ""} ${formatOrganizationSurveyTitle(a)}`.localeCompare(
        `${b.organization?.name || ""} ${formatOrganizationSurveyTitle(b)}`
      )
    ),
    [entries]
  );

  if (loading) {
    return <HStack justify="center" py={12}><Spinner size="lg" /></HStack>;
  }

  if (error) {
    return <Alert status="error" borderRadius="md"><AlertIcon />{error}</Alert>;
  }

  return (
    <VStack align="stretch" spacing={6}>
      <Box>
        <Heading size="lg">School reports</Heading>
        <Text color={mutedColor} mt={2}>
          Choose a school and survey cycle to open its full report.
        </Text>
      </Box>

      {sortedEntries.length === 0 ? (
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <Text color={mutedColor}>No reports are available for this network yet.</Text>
          </CardBody>
        </Card>
      ) : (
        <VStack align="stretch" spacing={4}>
          {sortedEntries.map((survey) => (
            <Card key={`${survey.organization?.id}-${survey.organization_survey_id}`} bg={cardBg} borderWidth="1px" borderColor={borderColor}>
              <CardBody>
                <VStack align="start" spacing={3}>
                  <Box>
                    <Heading size="md">{survey.organization?.name || "School"}</Heading>
                    <Text color={mutedColor} fontSize="sm" mt={1}>
                      {formatOrganizationSurveyTitle(survey) || "Survey"}
                    </Text>
                  </Box>
                  <Button
                    colorScheme="blue"
                    variant="outline"
                    onClick={() => navigate(
                      `/app/network/${networkId}/results/${survey.organization_survey_id}`,
                      {
                        state: {
                          surveyTitle: formatOrganizationSurveyTitle(survey),
                          survey,
                        },
                      }
                    )}
                  >
                    View full results
                  </Button>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </VStack>
      )}
    </VStack>
  );
};

export default NetworkResultsPage;
